<template>
  <IntercomLayout>
    <template #topbar>
      <FaraidTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @select-case="(c) => $router.push(`/cases/${c.id}`)"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="cases-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('case.cases') }}</h1>
          <p class="page-subtitle">{{ $t('case.subtitle') }}</p>
        </div>
        <IntercomButton variant="primary" @click="$router.push('/cases/new')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('case.newCase') }}</span>
        </IntercomButton>
      </div>

      <!-- Filters -->
      <div class="filters-bar">
        <IntercomInput
          v-model="searchQuery"
          type="search"
          :placeholder="$t('common.search') + '...'"
          class="search-input"
        />
        <select v-model="statusFilter" class="filter-select">
          <option value="">{{ $t('common.all') }} {{ $t('case.status') }}</option>
          <option value="submitted">{{ $t('status.submitted') }}</option>
          <option value="under_review">{{ $t('status.underReview') }}</option>
          <option value="info_requested">{{ $t('status.infoRequested') }}</option>
          <option value="evaluated">{{ $t('status.evaluated') }}</option>
          <option value="delivered">{{ $t('status.delivered') }}</option>
          <option value="disputed">{{ $t('status.disputed') }}</option>
          <option value="cancelled">{{ $t('status.cancelled') }}</option>
        </select>
      </div>

      <!-- Cases Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="filteredCases"
          :loading="isLoading"
          hoverable
          clickable
          @row-click="handleRowClick"
        >
          <template #cell-status="{ value }">
            <CaseStatusBadge :status="value" />
          </template>
          <template #cell-heirs="{ row }">
            {{ row.heirs?.length || 0 }}
          </template>
          <template #cell-submitted_at="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-scholar="{ row }">
            {{ row.scholar?.name || '-' }}
          </template>
        </IntercomTable>
        <p v-if="!isLoading && filteredCases.length === 0" class="empty-text">
          {{ $t('case.noCases') }}
        </p>
      </IntercomCard>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useNavigation } from '../composables/useNavigation.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomInput,
} from '@shared/components/intercom'
import FaraidTopbar from '../components/FaraidTopbar.vue'
import CaseStatusBadge from '../components/CaseStatusBadge.vue'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listCases } = useFaraid()
const { navSections } = useNavigation()

const cases = ref([])
const isLoading = ref(false)
const searchQuery = ref('')
const statusFilter = ref('')

const columns = [
  { key: 'case_number', title: t('case.caseNumber'), width: '130px' },
  { key: 'deceased_name', title: t('case.deceased'), width: '160px' },
  { key: 'school', title: t('case.school'), width: '100px' },
  { key: 'heirs', title: t('case.heirs'), width: '70px', align: 'center' },
  { key: 'status', title: t('case.status'), width: '130px' },
  { key: 'scholar', title: t('case.scholar'), width: '130px' },
  { key: 'submitted_at', title: t('case.date'), width: '120px' },
]

const filteredCases = computed(() => {
  let result = cases.value
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    result = result.filter(
      (c) =>
        c.case_number?.toLowerCase().includes(q) ||
        c.deceased_name?.toLowerCase().includes(q)
    )
  }
  return result
})

const formatDate = (date) => {
  if (!date) return ''
  return dayjs(date).format('DD MMM YYYY')
}

const fetchCases = async () => {
  isLoading.value = true
  try {
    const params = { limit: 100 }
    if (statusFilter.value) params.status = statusFilter.value
    const response = await listCases(params)
    cases.value = response.data || response.items || []
  } catch (error) {
    console.error('Failed to fetch cases:', error)
  } finally {
    isLoading.value = false
  }
}

const handleRowClick = (row) => {
  router.push(`/cases/${row.id}`)
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusiness()
  fetchCases()
})

// Re-fetch when status filter changes
import { watch } from 'vue'
watch(statusFilter, () => fetchCases())
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.cases-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.filters-bar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.search-input {
  flex: 1;
  max-width: 320px;
}

.filter-select {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-white);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .cases-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .filters-bar {
    flex-direction: column;
  }

  .search-input {
    max-width: 100%;
  }
}
</style>
